import { FC, Fragment } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { SideBarRoutes } from 'features/sidebar/types/SideBarRoutes';
import { useTypedSelector } from 'common/hooks/store';
import { ChildRoute } from 'common/types/RouteChildren';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'app/store';
import {
  closeSideBarAccordion,
  onToggleAccordion
} from 'features/sidebar/sidebarTogglingSlice';
import { debugToggleByType } from 'features/internal/internal-settings.slice';
import { ToggleType } from 'features/internal/internal.models';

const menuItemStyle = 'h-14 cursor-pointer flex items-center px-8 mx-2';
const memuItemStyleMinimal =
  'h-14 cursor-pointer flex items-center justify-center px-8 mx-5 md:px-2.5 md:m-2.5';

const SidebarMenuItemLegacy: FC<{
  data: SideBarRoutes;
  index: number;
  isSidebarOpen: boolean;
}> = ({
  data,
  index,
  isSidebarOpen
}: {
  data: SideBarRoutes;
  index: number;
  isSidebarOpen: boolean;
}) => {
  const dispatch = useDispatch();

  const { indexOfOpenAccordion } = useSelector(
    (state: RootState) => state.sidebar
  );

  const { siteId } = useSelector((state: RootState) => state.selectedSite);

  const { isPocModeActive } = useTypedSelector(
    (state: RootState) => state.pocToggle
  );
  const location = useLocation();
  const currentPathName = location.pathname.substring(
    location.pathname.indexOf('/') + 1
  );

  const isRouteActive = (
    currentPathName: string,
    sidebarItem: SideBarRoutes
  ): boolean => {
    const isRootPathActive =
      currentPathName === '' && sidebarItem.path === 'dashboard';
    return (
      isRootPathActive ||
      sidebarItem.path === currentPathName ||
      sidebarItem.children.some(
        (child: ChildRoute) => child.path === currentPathName
      ) ||
      currentPathName.startsWith(sidebarItem.path as string)
    );
  };

  const getIconColour = (
    currentPathName: string,
    sidebarItem: SideBarRoutes
  ): string =>
    isRouteActive(currentPathName, sidebarItem) ? 'text-tertiary-600' : '';

  const getButtonColour = (
    currentPathName: string,
    sidebarItem: SideBarRoutes
  ): string =>
    isRouteActive(currentPathName, sidebarItem) ? 'bg-navy-dark' : '';

  const getFlatIcon = (label: string): string => {
    switch (label) {
      case 'Dashboard':
        return 'ra-icons-generic-menu';
      case 'Advanced Reports':
        return 'ra-icons-marketing-arrow-chart-1';
      case 'Reports':
        return 'ra-icons-generic-page-1';
      case 'Conversions':
        return 'ra-icons-marketing-analytics';
      case 'Revenue':
        return 'ra-icons-marketing-money-bag';
      case 'Opportunities':
        return 'ra-icons-marketing-sales-funnel-1';
      case 'Paths':
        return 'ra-icons-marketing-breadcrumb-2';
      case 'Marketing Mix Modelling':
        return 'ra-icons-marketing-ai';
      default:
        return 'ra-icons-generic-question-sign';
    }
  };

  const shouldShowPocLinks = (
    isLinkPocType: boolean | undefined,
    isPocModeActive: boolean
  ): boolean => {
    return (
      isLinkPocType === undefined ||
      (isLinkPocType !== undefined && isLinkPocType && isPocModeActive)
    );
  };

  const shouldShowWhiteListedSiteLinks = (
    whiteListedSites: string[] | undefined,
    siteId: string
  ): boolean => {
    return (
      whiteListedSites === undefined ||
      (whiteListedSites !== undefined &&
        whiteListedSites.length > 0 &&
        whiteListedSites.includes(siteId))
    );
  };

  const RenderSingleMenuItem = (menuItem: SideBarRoutes) => (
    <NavLink
      onClick={() => dispatch(closeSideBarAccordion())}
      title={menuItem.label}
      to={menuItem.path ?? '*'}
      className=" text-off-gray mb-2 block"
    >
      <div
        key={`sidebar-single-${menuItem.label}`}
        className={`transition-all ${
          menuItem.active ? 'backdrop-brightness-125' : ''
        } ${
          isSidebarOpen ? menuItemStyle : memuItemStyleMinimal
        }  ${getButtonColour(
          currentPathName,
          menuItem
        )} rounded hover:backdrop-brightness-125`}
      >
        <i
          className={`flex items-center justify-center w-6 transition-all text-xl ${
            isSidebarOpen ? 'mr-5' : ''
          } ${getIconColour(currentPathName, menuItem)} ${getFlatIcon(
            menuItem.label
          )}`}
        ></i>
        <span className={`text-sm ${isSidebarOpen ? 'block' : 'md:hidden'}`}>
          {menuItem.label}
        </span>
      </div>
    </NavLink>
  );

  const SingleMenuItem = (menuItem: SideBarRoutes) => {
    return (
      <>
        {shouldShowPocLinks(menuItem?.poc, isPocModeActive) &&
        shouldShowWhiteListedSiteLinks(menuItem?.whiteListedSites, siteId)
          ? RenderSingleMenuItem(menuItem)
          : null}
      </>
    );
  };

  const MultiMenuItem = (menuItem: SideBarRoutes, isPocModeActive: boolean) => {
    const handleClick = () => {
      dispatch(onToggleAccordion(index));
    };

    const CreateMultipleLevel = () => {
      return (
        <>
          <div className="flex-col rounded">
            {menuItem.children.map(
              (childItem: ChildRoute, childIndex: number) => {
                return (
                  <Fragment key={`nav-${childIndex}`}>
                    {shouldShowPocLinks(menuItem.poc, isPocModeActive) &&
                    shouldShowPocLinks(childItem.poc, isPocModeActive) &&
                    shouldShowWhiteListedSiteLinks(
                      menuItem?.whiteListedSites,
                      siteId
                    ) ? (
                      <NavLink
                        to={childItem.path}
                        className={`${
                          isSidebarOpen ? menuItemStyle : memuItemStyleMinimal
                        } pl-14 text-off-gray bg-navy-dark hover:bg-navy hover:brightness-125 transition-all ${
                          childIndex === menuItem.children.length - 1
                            ? 'rounded-b'
                            : ''
                        }`}
                      >
                        <div
                          className="text-sm"
                          key={`side-menu-child-item-${childIndex}`}
                        >
                          {isSidebarOpen ? childItem.label : ''}
                        </div>
                      </NavLink>
                    ) : null}
                  </Fragment>
                );
              }
            )}
          </div>
        </>
      );
    };

    const addChevron = () => {
      return isSidebarOpen ? (
        <i
          className={`ra-icons-generic-down-arrow text-xs flex flex-row transition-all 
      ${indexOfOpenAccordion === index ? 'rotate-180' : 'rotate-0'}`}
        ></i>
      ) : null;
    };

    return (
      <>
        {shouldShowPocLinks(menuItem?.poc, isPocModeActive) &&
        shouldShowWhiteListedSiteLinks(menuItem?.whiteListedSites, siteId) ? (
          <>
            <div
              title={menuItem.label}
              key={`sidebar-multi-${menuItem.label}`}
              className={`${
                isSidebarOpen
                  ? `${menuItemStyle} justify-between`
                  : `h-14 cursor-pointer flex items-center justify-center px-10 mx-5 md:px-2.5 md:mx-2.5 md:mt-2.5`
              } pr-5 transition-all ${
                indexOfOpenAccordion === index
                  ? 'bg-navy-dark rounded-b-none'
                  : ''
              } ${getButtonColour(
                currentPathName,
                menuItem
              )} hover:backdrop-brightness-125 rounded text-off-gray`}
              onClick={handleClick}
            >
              <span className="flex items-center">
                <i
                  className={`flex items-center justify-center w-6 transition-all text-xl ${
                    isSidebarOpen ? 'mr-5' : ''
                  } ${getIconColour(currentPathName, menuItem)} ${getFlatIcon(
                    menuItem.label
                  )}`}
                ></i>
                <span className="text-sm">
                  {isSidebarOpen ? menuItem.label : ''}
                </span>
              </span>
              <span>{addChevron()}</span>
            </div>
            <div
              className={
                `flex-col transition-all overflow-hidden ` +
                (indexOfOpenAccordion === index ? 'max-h-screen' : 'max-h-0') +
                (isSidebarOpen ? ' mb-2' : '')
              }
            >
              <CreateMultipleLevel />
            </div>
          </>
        ) : null}
      </>
    );
  };

  const MenuItem = (menuItem: SideBarRoutes, isPocModeActive: boolean) => {
    if (menuItem.children.length > 0) {
      return MultiMenuItem(menuItem, isPocModeActive);
    }

    return SingleMenuItem(menuItem);
  };

  return <>{MenuItem(data, isPocModeActive)}</>;
};

export default SidebarMenuItemLegacy;
